import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useSearchParams } from "react-router-dom";
import FilterComponent from "../../../ReusableFIlterComponent/FilterComponent";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import {
  IP_FILTER_OPTIONS,
  MULTI_BRAND_FILTER_OPTIONS,
  QUALIFIED_FILTER_OPTIONS,
} from "../constants";

interface LeadsFilterItemsProps {
  data: LeadsManagementData;
}

// FilterComponent mirrors its own selections into this URL param and
// reapplies it on every searchParams change (see its `filtersString`
// useEffect) — clearing filters has to delete it too, or that effect
// reapplies the stale encoded selection right after we reset state below.
const FILTER_NAME_URL = "leadsFilters";

export default function LeadsFilterItems({ data }: LeadsFilterItemsProps) {
  const {
    brand,
    brandOptions,
    setBrand,
    search,
    setSearch,
    qualified,
    ipFilter,
    minScore,
    multiBrand,
    setQualified,
    setIpFilter,
    setMultiBrand,
    isLoading,
  } = data;
  const [, setSearchParams] = useSearchParams();

  const hasActiveFilters =
    brand !== "All" ||
    qualified !== "All" ||
    ipFilter !== "All" ||
    multiBrand !== "All" ||
    search !== "";

  function handleClearFilters() {
    setBrand("All");
    setQualified("All");
    setIpFilter("All");
    setMultiBrand("All");
    setSearch("");
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete(FILTER_NAME_URL);
      return next;
    });
  }

  // Same shared FilterComponent (Paper popup, "Select a ..." header, radio
  // list, purple active-state) as the Brands module's Status/Affiliate
  // System/Company/Website filters — see BrandsFilterItems.tsx. Each of
  // these is a single exclusive value server-side (qualified_by_brand/
  // has_duplicate_ip/reported_to_multiple_brands), so multiSelect is false
  // (radio, not checkbox) and the default ("All"/"Any") is left out of the
  // option list — same convention Brands uses (no "All" entry; the row's
  // own Clear Filter resets back to it).
  const filterConfig = [
    {
      key: "qualified",
      name: "Qualified by Brand",
      label: "Select Qualified By Brand",
      selectedOption: qualified !== "All" ? [qualified] : [],
      filterName: "qualified",
      onSelectionChange: (updated: string[]) =>
        setQualified(updated[0] ?? "All"),
      count: qualified !== "All" ? 1 : 0,
      colorSelected: "#9C27B0",
      multiSelect: false,
      showSearch: false,
      options: QUALIFIED_FILTER_OPTIONS.filter(
        (option) => option.value !== "All",
      ),
    },
    {
      key: "ip",
      name: "IP Address",
      label: "Select an IP Address filter",
      selectedOption: ipFilter !== "All" ? [ipFilter] : [],
      filterName: "ip",
      onSelectionChange: (updated: string[]) =>
        setIpFilter(updated[0] ?? "All"),
      count: ipFilter !== "All" ? 1 : 0,
      colorSelected: "#9C27B0",
      multiSelect: false,
      showSearch: false,
      options: IP_FILTER_OPTIONS.filter((option) => option.value !== "All"),
    },
    {
      key: "multiBrand",
      name: "Multi Brand",
      label: "Select a Multi Brand filter",
      selectedOption: multiBrand !== "All" ? [multiBrand] : [],
      filterName: "multiBrand",
      onSelectionChange: (updated: string[]) =>
        setMultiBrand(updated[0] ?? "All"),
      count: multiBrand !== "All" ? 1 : 0,
      colorSelected: "#9C27B0",
      multiSelect: false,
      showSearch: false,
      options: MULTI_BRAND_FILTER_OPTIONS.filter(
        (option) => option.value !== "All",
      ),
    },
  ];

  return (
    <Paper
      sx={{
        mt: 3,
        boxShadow: "none",
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
      }}
    >
      <TextField
        label="Search"
        placeholder="Name, email, user unique ID, ID or IP"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        color="secondary"
        disabled={isLoading}
        data-tracker="925"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "action.active" }} />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                size="small"
                disabled={isLoading}
                onClick={() => setSearch("")}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{ width: 320 }}
      />

      <TextField
        select
        label="Brand Name"
        value={brand}
        onChange={(e) => setBrand(e.target.value)}
        size="small"
        color="secondary"
        disabled={isLoading}
        data-tracker="922"
        SelectProps={{
          MenuProps: { disablePortal: true },
          endAdornment:
            brand !== "All" ? (
              <IconButton
                aria-label="Clear brand filter"
                size="small"
                disabled={isLoading}
                sx={{ mr: 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setBrand("All");
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : undefined,
        }}
        sx={{ minWidth: 300 }}
      >
        {brandOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {/* FilterComponent's own trailing "Clear Filter" button is hidden here
          (pure CSS on our own wrapper — FilterComponent.tsx itself is left
          untouched) so Minimum Lead Score can sit between Multi Brand and a
          single Clear Filters button placed last in the row, matching the
          requested sequence. */}
      <Box
        data-tracker="1041"
        sx={{ "& > div > button:last-of-type": { display: "none" } }}
      >
        <FilterComponent
          filterConfig={filterConfig}
          clearFilters={handleClearFilters}
          filterNameURL={FILTER_NAME_URL}
          disabled={isLoading}
        />
      </Box>

      {/* Disabled until lead_scoring_min is confirmed live on the backend —
          see the note in useLeadsManagementData.ts's fetchLeads. FilterComponent
          has no disabled-trigger support, so this stays a plain non-interactive
          button styled to match its triggers until it's ready to be wired up
          the same way as Qualified/IP/Multi Brand above. */}
      <Button
        disabled
        title="Coming soon — pending backend support"
        endIcon={<KeyboardArrowDownIcon />}
        data-tracker="963"
      >
        Minimum Lead Score{minScore !== "0" ? ` (${minScore}+)` : ""}
      </Button>

      <Button
        onClick={handleClearFilters}
        disabled={isLoading || !hasActiveFilters}
        sx={{
          color: "text.primary",
          textTransform: "none",
          whiteSpace: "nowrap",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        Clear Filters
      </Button>
    </Paper>
  );
}
